import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getLicenseSalesGuide } from '@/api/license/getLicense';
import BasicButton from '@/components/Common/Button/BasicButton';
import MenuHeader from '@/components/Header/MenuHeader';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import DefaultHelmet from '@/metadatas/DefaultHelmet';

interface DocumentItem {
  name: string;
  note?: string;
}

interface Step {
  stepNumber: number;
  title: string;
  description?: string;
  note?: string;
  sellerDocuments?: DocumentItem[];
  buyerDocuments?: DocumentItem[];
}

interface CostItem {
  title: string;
  description: string;
  estimatedCost?: string;
}

interface WarningItem {
  title: string;
  description: string;
  example?: string;
  conditions?: {
    title: string;
    description: string;
    example?: {
      status: string;
      description: string;
    }[];
  }[];
  exception?: {
    title: string;
    description: string;
  };
}

interface FAQAnswer {
  point?: string;
  point2?: string;
  introduction?: string;
  stepContent?: string[];
  subIntroduction?: string;
  steps?: Step[];
  costs?: CostItem[];
  warnings?: WarningItem[];
  notes?: string[];
  tip?: string[];
}

interface FAQItem {
  question: string;
  answer: FAQAnswer;
  isExpanded?: boolean;
}

interface LicenseSalesGuideResponse {
  result: string;
  message: string;
  title: string;
  guideList: FAQItem[];
}

// 더미 데이터
const dummyData: LicenseSalesGuideResponse = {
  result: 'Y',
  message: '',
  title: '번호판 판매 가이드',
  guideList: [
    {
      question: '번호판 판매 시 준비 절차와 소요기간은 어떻게 되나요?',
      answer: {
        introduction: '영업용 번호판(개인 영업용 번호판) 구매는 다음과 같은 순서로 진행됩니다.',
        steps: [
          {
            stepNumber: 1,
            title: '서류 준비',
            sellerDocuments: [
              { name: '자동차등록증' },
              { name: '운송사업허가증' },
              { name: '자동차양도증명서', note: '매도자, 매수자 상호간 함께 작성' },
              { name: '양도, 양수 계약서', note: '매도자, 매수자 함께 작성' },
              { name: '양도, 양수 신고서', note: '매도자, 매수자 함께 작성' },
              { name: '화물운송종사자격증명', note: '협회발급' },
              { name: '인감증명서 1통' },
              { name: '인감증명서 1통', note: '매도용' },
            ],
            buyerDocuments: [
              { name: '기본증명서 1통' },
              { name: '주민등록등본 1통' },
              { name: '인감증명서 2통' },
              { name: '운전경력증명서 1통' },
              { name: '인감도장' },
              { name: '운전면허증 사본' },
              { name: '화물운송 종사자격증 사본' },
              { name: '증명사진 2장' },
            ],
          },
          {
            stepNumber: 2,
            title: '지자체 접수',
            description: '양수인(구매자) 주소지 관할 시·군·구청에서 "영업용 번호판 양도·양수 신청"을 접수합니다.',
            note: '직트럭에서는 이 과정을 빠르고 정확하게 진행할 수 있도록 서류 안내 및 확인을 도와드립니다.',
          },
          {
            stepNumber: 3,
            title: '처리 기간',
            description:
              '행정 절차는 약 10일 전후 소요되며, 지자체별 업무 일정이나 공휴일에 따라 다소 차이가 있을 수 있습니다.',
          },
          {
            stepNumber: 4,
            title: '차량 명의이전 및 대폐차 필요 시',
            description:
              '구매자가 차량+번호판을 함께 구매한 게 아니라면 구매자의 차량으로 번호판 변경신청을 하기위해 관할 협회에서 대, 폐차라는 행정 절차가 추가됩니다.',
          },
        ],
        notes: [
          '개인 영업용 번호판은 양도·양수 후 6개월 동안 다시 양도할 수 없습니다.',
          '딜러나 매매상사는 직접 명의 변경이 불가해, 구매자가 결정될 때까지 판매자 서류만 보관하며 대기합니다.',
        ],
      },
      isExpanded: false,
    },
    {
      question: '차량과 번호판을 따로 판매하면 추가 비용이 발생하나요?',
      answer: {
        point: '아닙니다.',
        introduction:
          '차량과 번호판을 함께 판매하든 따로 판매하든, 판매자에게 별도의 수수료나 행정비용은 발생하지 않습니다.\n즉, 판매 절차 자체는 동일합니다.\n\n다만, 영업용 번호판을 양도할 때는 법적으로 "허가권(번호판)"을 함께 이전하는 것이기 때문에\n 번호판만 판매하더라도 행정상 차량이 구매자 명의로 함께 이전되는 구조입니다.\n\n이후 차량을 다시 본인 명의로 되돌리려면 취득·등록세, 차고지 등록비, 보험 등 부대비용이 발생하게 됩니다.',
        tip: [
          '차량도 함께 처분할 계획이라면, 번호판 양도 완료 시점에 차량 매각을 맞추는 것이 가장 효율적입니다.',
          '이렇게 하면 불필요한 세금과 행정비용을 최소화 할 수 있습니다.',
        ],
      },

      isExpanded: false,
    },
    {
      question: '차량은 남겨두고 번호판만 판매할 수 있나요?',
      answer: {
        point2: '네, 가능합니다.',
        introduction: `다만 영업용 번호판은 행정 절차상 차량과 함께 묶여 처리되므로, 번호판만 양도하더라도 차량 명의가 함께 구매자 명의로 변경됩니다.\n\n또한 개인 영업용 번호판은 양도·양수 후 6개월 이내 재양도가 불가능하며, 딜러나 매매상사는 실제 구매자가 정해질 때까지 판매자의 서류만 보관합니다.\n\n번호판만 양도 후 차량을 다시 본인 명의로 돌리려면 취득세·등록세·보험료 등 추가 비용이 발생하며, 차량 처분을 함께 계획하고 있다면 번호판 양도 시점과 매각 일정을 조율하는 것이 좋습니다.`,
        tip: [
          '번호판이 남아 있는 상태에서는 차량 명의 이전이 불가능하므로, 차량을 판매하려면 반드시 번호판을 먼저 양도해 주세요.',
        ],
      },
      isExpanded: false,
    },
    {
      question: '번호판은 남겨두고 차량만 먼저 판매할 수 있나요?',
      answer: {
        point: '불가능합니다.',
        introduction:
          '영업용 번호판은 "물건"이 아니라 "운행 허가권"으로 분류되기 때문에, 번호판만 따로 보유하거나 남겨두는 것은 법적으로 허용되지 않습니다.\n\n영업용 번호판은 "운행 허가권" 으로 분류되기 때문에 번호판만 따로 보유하거나 남겨두는 것은 법적으로 허용되지 않습니다.\n\n차량을 판매하려면 반드시 번호판을 먼저 양도(처분) 해야 하며, 번호판이 부착된 상태의 차량은 일반 차량처럼 자유롭게 매매나 명의 이전이 불가능합니다.\n\n번호판을 남겨둔 채 차량을 먼저 처분할 경우, 행정상 불법 상태로 간주될 수 있습니다.',
        tip: [
          '영업용 차량을 처분하실 때는 "번호판 처분 → 차량 매각" 순서가 반드시 지켜져야 합니다.',
          '번호판이 남아 있는 한 차량 명의이전이 불가능합니다.',
        ],
      },

      isExpanded: false,
    },
    {
      question: '영업용 번호판 거래에서 직트럭은 어떤 역할을 하나요?',
      answer: {
        introduction:
          '영업용 번호판(개별화물) 거래는 단순한 사고팔기가 아닌, 복잡한 행정 절차가 필요한 ‘허가권 양도’ 과정입니다.\n\n년식 제한, 증톤 여부 등 조건이 까다롭고 사건·사고 위험도 높은 거래이기 때문에, 개인이 직접 진행할 경우 서류 누락이나 접수 오류로 허가권이 말소될 위험이 있습니다.\n\n 직트럭은 이러한 위험을 예방하고, 판매자와 구매자가 안전하고 투명하게 거래할 수 있도록 전 과정을 전문적으로 중개·대행합니다.',
        // stepContent: [
        //   '서류 검토 및 행정 절차 안내',
        //   '지자체 접수 과정의 오류 방지',
        //   '거래 과정에서의 안전한 잔금 처리',
        //   '판매자·구매자 간 신뢰 보증',
        // ],
        // subIntroduction: '전 과정을 전문적으로 중개·대행합니다.',
        tip: [
          '영업용 번호판은 한 번의 실수로도 허가권이 말소될 수 있는 민감한 자산입니다.',
          '안전하고 확실한 거래를 원하신다면, 반드시 직트럭의 전문 중개 서비스를 이용하세요.',
          '중개 수수료 15만원으로 안전하고, 투명한 거래를 책임지겠습니다.',
        ],
      },
      isExpanded: false,
    },
    {
      question: '차량년식에 상관없이 영업용 번호판은 아무거나 구입해서 달 수 있나요?',
      answer: {
        point: '불가능합니다.',
        introduction:
          '영업용 번호판(개별화물)은 단순한 차량 번호가 아닌 ‘유상운송 허가권’으로, 운수사업 허가증을 양수받는 절차입니다.\n 현재는 신규 허가가 불가능해, 기존 허가증을 가진 사람으로부터 허가증과 차량을 함께 인수하는 형태로만 거래가 가능합니다.',
        warnings: [
          {
            title: '대·폐차(대차) 조건',
            description: '대폐차는 아무 차량이나 가능한 것이 아니라, 다음과 같은 조건을 모두 충족해야 합니다.',
            conditions: [
              {
                title: '톤수 구분',
                description: '소형 / 중형 / 대형 동일해야 함',
              },
              {
                title: '년식 구분',
                description: '내 차량보다 더 예전 년식에 부착된 번호판만 가능',
                example: [
                  { status: 'N', description: '내차량 2020년11월30일 / 번호판차량 2020년12월30일' },
                  { status: 'Y', description: '내차량 2020년11월30일 / 번호판차량 2020년10월30일' },
                ],
              },
            ],
            exception: {
              title: '예외',
              description: '출고 3년 이내의 신차급 차량은 예외 인정 (예: 25년 10월 차량 → 22년 10월 번호판도 등록가능)',
            },
          },
          {
            title: '허가증(번호판) 유지 관련 주의사항',
            description:
              '허가증은 한 번 양수받으면 기한 제한 없이 본인 명의로 유지할 수 있습니다.\n하지만 운수법 위반 시(예: 음주운전으로 인한 면허 취소)에는 허가증 또한 취소될 수 있습니다. 따라서 문제가 발생하기 전에 양도 또는 처분을 신속히 진행하는 것이 안전합니다.',
          },
        ],
        tip: [
          '영업용 번호판은 단순한 번호가 아닌 운송사업의 허가권입니다. 번호판만 따로 사거나, 조건을 무시하고 대폐차를 시도하면 허가 말소나 행정처분의 위험이 있습니다. 반드시 직트럭을 통해 조건 검토와 절차 안내를 받은 뒤 진행하세요.',
        ],
      },
      isExpanded: false,
    },
  ],
};

const LicenseSalesGuide = () => {
  const navigate = useNavigate();

  const [faqList, setFaqList] = useState<FAQItem[]>([]);
  const [openFAQId, setOpenFAQId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isBrokerageInfoOpen, setIsBrokerageInfoOpen] = useState<boolean>(true);

  useEffect(() => {
    const fetchLicenseBuyGuide = async () => {
      try {
        setIsLoading(true);
        // TODO: 실제 API 연동 시 아래 주석 해제
        // const data: LicenseSalesGuideResponse = await getLicenseSalesGuide();
        // if (data?.guideList && data.guideList.length > 0) {
        //   setFaqList(data.guideList);
        //   // 첫 번째 항목을 기본으로 열기
        //   // setOpenFAQId(0);
        // }

        // 더미 데이터 사용
        if (dummyData?.guideList && dummyData.guideList.length > 0) {
          setFaqList(dummyData.guideList);
          // setOpenFAQId(0);
        }
      } catch (error) {
        console.error('FAQ 데이터를 불러오는 중 오류가 발생했습니다:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLicenseBuyGuide();
  }, []);

  return (
    <>
      <DefaultHelmet page="license" />

      <MenuHeader title="번호판 판매 가이드"></MenuHeader>
      <div className="w-full pt-[60px] pb-[70px] px-4 text-gray-8">
        <div className="bg-secondaryRed-light border border-grey-5 rounded-lg shadow-rightBottom p-3 mb-3 mt-3">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setIsBrokerageInfoOpen(!isBrokerageInfoOpen)}
          >
            <p className="text-gray-10">
              번호판 판매는 <span className="text-secondaryRed-1 font-semibold">직트럭</span>이 직접 중개합니다.
            </p>
            {isBrokerageInfoOpen ? (
              <ChevronUpIcon color={colors.gray[7]} width={20} height={20} className="shrink-0 ml-2" />
            ) : (
              <ChevronDownIcon color={colors.gray[7]} width={20} height={20} className="shrink-0 ml-2" />
            )}
          </div>
          {isBrokerageInfoOpen && (
            <>
              <div className="flex flex-col gap-2 mb-4 mt-4">
                {[
                  '서류 검토 및 행정 절차 안내',
                  '지자체 접수 과정의 오류 방지',
                  '거래 과정에서의 안전한 잔금 처리',
                  '판매자·구매자 간 신뢰 보증',
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckIcon color={colors.secondaryRed[1]} width={20} height={20} className="shrink-0 mt-0.5" />
                    <span className="text-gray-8">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-grey-7 text-[14px]">중개 수수료 15만원(VAT 별도) 발생합니다.</p>
            </>
          )}
        </div>

        <div className="py-4">
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              src={`https://www.youtube.com/embed/mFQNJte5p-k?start=2`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>

        <div className="flex flex-col gap-0">
          {faqList.map((faq, index) => {
            const isOpen = openFAQId === index;
            return (
              <div key={index} className={`border-b border-gray-4 bg-white`}>
                <div
                  className="flex items-start justify-between py-4 px-2 cursor-pointer"
                  onClick={() => {
                    const newOpenFAQId = isOpen ? null : index;
                    if (!isOpen) {
                      setIsBrokerageInfoOpen(false);
                    } else {
                      setIsBrokerageInfoOpen(true);
                    }
                    setOpenFAQId(newOpenFAQId);
                  }}
                >
                  <div className="flex items-start gap-2 flex-1">
                    <span className="text-secondaryRed-1 font-semibold text-base">Q</span>
                    <span className="text-gray-10 text-base font-medium">{faq.question}</span>
                  </div>
                  <div className="ml-2 mt-[2px]">
                    {isOpen ? (
                      <ChevronUpIcon color={colors.gray[7]} width={20} height={20} />
                    ) : (
                      <ChevronDownIcon color={colors.gray[7]} width={20} height={20} />
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div className="px-1 pb-4">
                    <div className="pl-6 pr-6 text-gray-8 text-sm leading-relaxed">
                      {faq.answer.point && (
                        <p className="mb-1 whitespace-pre-line font-semibold text-secondaryRed-1">{faq.answer.point}</p>
                      )}
                      {faq.answer.point2 && (
                        <p className="mb-1 whitespace-pre-line font-semibold text-secondaryBlue-1">
                          {faq.answer.point2}
                        </p>
                      )}
                      {faq.answer.introduction && <p className="mb-4 whitespace-pre-line">{faq.answer.introduction}</p>}

                      {/* stepContent 체크마크 리스트 (다섯 번째 질문) */}
                      {faq.answer.stepContent && (
                        <div className="mb-4 flex flex-col gap-2">
                          {faq.answer.stepContent.map((step, stepIndex) => (
                            <div key={stepIndex} className="flex items-start gap-2">
                              <CheckIcon
                                color={colors.secondaryBlue[1]}
                                width={20}
                                height={20}
                                className="shrink-0 mt-0.5"
                              />
                              <span className="text-gray-8">{step}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {faq.answer.subIntroduction && (
                        <p className="mb-4 whitespace-pre-line">{faq.answer.subIntroduction}</p>
                      )}

                      {/* 단계별 정보 (첫 번째 질문) */}
                      {faq.answer.steps && (
                        <div className="flex flex-col gap-4">
                          {faq.answer.steps.map((step, stepIndex) => (
                            <div key={stepIndex} className="flex flex-col gap-2 bg-secondaryRed-faq p-4 rounded-lg">
                              <div className="font-semibold text-secondaryRed-1">
                                ◆ {step.stepNumber}단계. {step.title}
                              </div>
                              {step.sellerDocuments && (
                                <div className="ml-2">
                                  <div className="font-medium text-gray-9 mb-1">[판매자]</div>
                                  <ul className="list-disc list-inside ml-2 space-y-1">
                                    {step.sellerDocuments.map((doc, docIndex) => (
                                      <li key={docIndex} className="text-gray-8">
                                        {doc.name}
                                        {doc.note && <span className="text-gray-6"> ({doc.note})</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {step.buyerDocuments && (
                                <div className="ml-2 mt-2">
                                  <div className="font-medium text-gray-9 mb-1">[구매자]</div>
                                  <ul className="list-disc list-inside ml-2 space-y-1">
                                    {step.buyerDocuments.map((doc, docIndex) => (
                                      <li key={docIndex} className="text-gray-8">
                                        {doc.name}
                                        {doc.note && <span className="text-gray-6"> ({doc.note})</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {step.description && (
                                <div className="ml-2 whitespace-pre-line text-gray-8">{step.description}</div>
                              )}
                              {step.note && <div className="ml-2 text-gray-7 italic">{step.note}</div>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* 비용 정보 (두 번째 질문) */}
                      {faq.answer.costs && (
                        <div className="flex flex-col gap-3">
                          {faq.answer.costs.map((cost, costIndex) => (
                            <div key={costIndex} className="flex flex-col gap-1">
                              <div className="font-semibold text-gray-10">{cost.title}</div>
                              <div className="ml-2 whitespace-pre-line text-gray-8">{cost.description}</div>
                              {cost.estimatedCost && (
                                <div className="ml-2 text-gray-7 font-medium">예상 비용: {cost.estimatedCost}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* 주의사항 (세 번째, 네 번째 질문) */}
                      {faq.answer.warnings && (
                        <div className="flex flex-col gap-4">
                          {faq.answer.warnings.map((warning, warningIndex) => (
                            <div key={warningIndex} className="flex flex-col gap-2 border border-gray-4 rounded-lg p-4">
                              <div className="font-semibold text-gray-10">
                                {warningIndex + 1}. {warning.title}
                              </div>
                              <div className="ml-2 whitespace-pre-line text-gray-8">{warning.description}</div>

                              {/* 조건 목록 (4번째 질문) */}
                              {warning.conditions && (
                                <div className="ml-2 mt-2 flex flex-col gap-3 ">
                                  {warning.conditions.map((condition, conditionIndex) => (
                                    <div
                                      key={conditionIndex}
                                      className="flex flex-col gap-1 bg-grey-1 border border-gray-3 rounded-lg p-4"
                                    >
                                      <div className="font-medium text-gray-9">
                                        <strong className="">📌 {condition.title}</strong> <br />
                                        {condition.description}
                                      </div>
                                      {condition.example && (
                                        <div className="ml-1 flex flex-col gap-1">
                                          {condition.example.map((ex, exIndex) => (
                                            <div key={exIndex} className={`flex items-start gap-1 text-gray-8 text-sm`}>
                                              <span
                                                className="font-semibold text-[14px]"
                                                style={{
                                                  color:
                                                    ex.status === 'Y'
                                                      ? colors.secondaryBlue[1]
                                                      : colors.secondaryRed[1],
                                                }}
                                              >
                                                {ex.status === 'Y' ? '✓' : '×'}
                                              </span>
                                              <span>
                                                {ex.description}
                                                {ex.status === 'N' && (
                                                  <span className="text-secondaryRed-1"> (등록불가)</span>
                                                )}
                                                {ex.status === 'Y' && (
                                                  <span className="text-secondaryBlue-1"> (등록가능)</span>
                                                )}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* 예외 사항 (4번째 질문) */}
                              {warning.exception && (
                                <div className="ml-2 mt-2 bg-secondaryBlue-faq p-4 rounded-lg">
                                  <div className="font-medium text-gray-9">
                                    <strong className="text-secondaryBlue-1">💡 {warning.exception.title}</strong>{' '}
                                    <br />
                                    {warning.exception.description}
                                  </div>
                                </div>
                              )}

                              {warning.example && <div className="ml-2 text-gray-7 italic">{warning.example}</div>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* 참고사항 (노트) */}
                      {faq.answer.notes && (
                        <div className="mt-4 p-4 bg-grey-2 border-l-[6px] border-grey-5">
                          <div className="font-semibold text-gray-10 mb-2">[참고사항]</div>
                          <ul className="list-disc list-inside space-y-1">
                            {faq.answer.notes.map((note, noteIndex) => (
                              <li key={noteIndex} className="text-gray-8">
                                {note}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* 참고사항 (팁) */}
                      {faq.answer.tip && (
                        <div className="mt-4 bg-secondaryYellow-0 border border-secondaryYellow-1 rounded-lg p-4">
                          <div className="font-semibold text-gray-10 mb-2">TIP</div>
                          <div className="flex flex-col gap-2">
                            {faq.answer.tip.map((tip, tipIndex) => (
                              <p key={tipIndex} className="text-gray-8 whitespace-pre-line">
                                {tip}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div
          className="flex flex-col gap-5 items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-0 left-0 right-0 h-fit max-w-[720px] min-w-[280px]
         bg-white z-30"
        >
          <div className="flex gap-2 w-full">
            <BasicButton
              name="이전"
              bgColor={colors.gray[0]}
              borderColor={colors.gray[5]}
              textColor={colors.gray[8]}
              fontSize={16}
              height={48}
              fontWeight="bold"
              onClick={() => navigate('/license')}
            />
            <BasicButton
              name="판매 문의하기"
              bgColor={colors.secondaryRed[1]}
              borderColor={colors.secondaryRed[1]}
              textColor={colors.gray[0]}
              fontSize={16}
              height={48}
              fontWeight="bold"
              onClick={() => navigate('/license/form')}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default LicenseSalesGuide;
